import { createReducer, on } from '@ngrx/store';
import { OrganizationBillingHistoryState } from '../../models/organizations/billing-history.model';
import * as BillingHistoryActions from '../../actions/organizations/billing-history.actions';
import * as AuthActions from '../../actions/auth.actions';

export const initialOrganizationBillingHistoryState: OrganizationBillingHistoryState = {
  entriesByOrgId: {},
  totalsByOrgId: {},
  loadingByOrgId: {},
  errorsByOrgId: {},
  paginationByOrgId: {},
  latestRequestIdByOrgId: {},
  latestRequestByOrgId: {}
};

export const organizationBillingHistoryReducer = createReducer(
  initialOrganizationBillingHistoryState,

  on(BillingHistoryActions.loadBillingHistory, (state, action) => {
    const { organizationId, requestId, pageIndex, cursor, limit, skipCount, reset } = action;

    return {
      ...state,
      entriesByOrgId: reset ? {
        ...state.entriesByOrgId,
        [organizationId]: []
      } : state.entriesByOrgId,
      totalsByOrgId: reset ? {
        ...state.totalsByOrgId,
        [organizationId]: undefined
      } : state.totalsByOrgId,
      loadingByOrgId: {
        ...state.loadingByOrgId,
        [organizationId]: true
      },
      errorsByOrgId: {
        ...state.errorsByOrgId,
        [organizationId]: null
      },
      paginationByOrgId: reset ? {
        ...state.paginationByOrgId,
        [organizationId]: {
          pageIndex: 0,
          currentCursor: null,
          nextCursor: null,
          cursors: [null],
          limit
        }
      } : state.paginationByOrgId,
      latestRequestIdByOrgId: {
        ...state.latestRequestIdByOrgId,
        [organizationId]: requestId
      },
      latestRequestByOrgId: {
        ...state.latestRequestByOrgId,
        [organizationId]: {
          pageIndex,
          cursor: cursor ?? null,
          limit,
          skipCount,
          reset
        }
      }
    };
  }),

  on(BillingHistoryActions.loadBillingHistorySuccess, (state, action) => {
    const { organizationId, requestId, pageIndex, cursor, limit, entries, total, nextCursor } = action;
    if (state.latestRequestIdByOrgId?.[organizationId] !== requestId) {
      return state;
    }

    const previousPagination = state.paginationByOrgId?.[organizationId];
    const cursors = [...(previousPagination?.cursors ?? [null])].slice(0, pageIndex + 1);
    cursors[pageIndex] = cursor ?? null;
    if (nextCursor) {
      cursors.push(nextCursor);
    }

    return {
      ...state,
      entriesByOrgId: {
        ...state.entriesByOrgId,
        [organizationId]: entries
      },
      totalsByOrgId: {
        ...state.totalsByOrgId,
        [organizationId]: total ?? state.totalsByOrgId?.[organizationId]
      },
      loadingByOrgId: {
        ...state.loadingByOrgId,
        [organizationId]: false
      },
      errorsByOrgId: {
        ...state.errorsByOrgId,
        [organizationId]: null
      },
      paginationByOrgId: {
        ...state.paginationByOrgId,
        [organizationId]: {
          pageIndex,
          currentCursor: cursor ?? null,
          nextCursor,
          cursors,
          limit
        }
      }
    };
  }),

  on(BillingHistoryActions.loadBillingHistoryFailure, (state, { organizationId, requestId, error }) => {
    if (state.latestRequestIdByOrgId?.[organizationId] !== requestId) {
      return state;
    }
    return {
      ...state,
      loadingByOrgId: {
        ...state.loadingByOrgId,
        [organizationId]: false
      },
      errorsByOrgId: {
        ...state.errorsByOrgId,
        [organizationId]: error
      }
    };
  }),

  on(AuthActions.logout, AuthActions.clearUserData, () => initialOrganizationBillingHistoryState)
);
